import { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn, minutesToHm } from '@eo/shared';

import { CalendarBlock } from './CalendarBlock';
import {
  BLOCK_STATE_LABELS,
  CALENDAR_HOUR_HEIGHT,
  CALENDAR_PX_PER_MINUTE,
  CALENDAR_SOURCE_NAMES,
  CALENDAR_SOURCE_STYLE,
} from './constants';
import {
  blockGeometry,
  calendarHours,
  calendarWindow,
  draftGeometry,
  etMinuteToTs,
  etMinutesOfDay,
  hourLabel,
  layoutBlocks,
  nowMarkerMinute,
  snapMinutes,
} from './helpers';
import type { TimelineRow } from './api/types';
import type { BlockPreview, BlockSpan, DayTimelineProps, DraftSelection } from './types';

export function DayTimeline({
  data,
  busyRowId,
  onConfirm,
  onDismiss,
  onDelete,
  onChangeDuration,
  onToggleBillable,
  onEdit,
  onMove,
  onDraw,
  pendingDraw,
  hiddenSources,
  onToggleSource,
  readOnly,
}: DayTimelineProps) {
  // Drawing an entry is the same gesture as moving one, read against the grid
  // instead of against a block: follow the pointer live, settle on release.
  const groundRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const drawing = useRef(false);
  // Read on release, which can land before the last move has re-rendered.
  const span = useRef<DraftSelection | null>(null);
  const [selection, setSelection] = useState<DraftSelection | null>(null);
  const [preview, setPreview] = useState<BlockPreview | null>(null);

  const { startMinute, endMinute } = calendarWindow(data.rows);
  const layout = layoutBlocks(
    data.rows.map((row) =>
      row.id === preview?.rowId
        ? { id: row.id, startTs: preview.startTs, durationMinutes: preview.durationMinutes }
        : row,
    ),
    startMinute,
    preview?.fraction === undefined ? undefined : { id: preview.rowId, fraction: preview.fraction },
  );

  const onPreview = (row: TimelineRow, span: BlockSpan | null) => {
    if (span === null) {
      setPreview(null);
      return;
    }
    const { clientX, ...rest } = span;
    const rect = groundRef.current?.getBoundingClientRect();
    const fraction =
      clientX === undefined || !rect?.width ? undefined : (clientX - rect.left) / rect.width;
    setPreview({ rowId: row.id, ...rest, fraction });
  };

  // The block stays where it was dropped until the save behind it settles, so
  // a logged entry does not spring back for the length of a round trip. Its
  // column goes back to the day's own order at once: the side was only ever
  // there to keep the block in hand from jumping under the pointer.
  const settle = (row: TimelineRow, save: () => void | Promise<unknown>) => {
    setPreview((prev) => (prev?.rowId === row.id ? { ...prev, fraction: undefined } : prev));
    const clear = () => setPreview((prev) => (prev?.rowId === row.id ? null : prev));
    void Promise.resolve(save()).finally(clear);
  };
  const typeNameById = new Map(data.taskTypes.map((t) => [t.id, t.name]));
  const hours = calendarHours(startMinute, endMinute);
  const gridHeight = ((endMinute - startMinute) / 60) * CALENDAR_HOUR_HEIGHT;
  const now = nowMarkerMinute(data.date, startMinute, endMinute);
  const offsetOf = (minute: number) => (minute - startMinute) * CALENDAR_PX_PER_MINUTE;
  const draft =
    selection !== null
      ? draftGeometry(selection, startMinute)
      : pendingDraw !== null
        ? {
            ...blockGeometry(pendingDraw, startMinute),
            durationMinutes: pendingDraw.durationMinutes,
          }
        : null;

  const minuteAt = (clientY: number) => {
    const gridTop = groundRef.current?.getBoundingClientRect().top ?? 0;
    return startMinute + snapMinutes((clientY - gridTop) / CALENDAR_PX_PER_MINUTE);
  };

  // A press that lands on a block belongs to that block: it is already both a
  // handle and a button, and drawing underneath it would answer the same
  // gesture twice.
  const onGroundDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly || busyRowId !== null || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('[data-block]') !== null) return;
    // Dragging across a page is also how text is selected, and a drawn entry
    // that leaves every title it crossed highlighted reads as a broken gesture.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const minute = minuteAt(event.clientY);
    span.current = { from: minute, to: minute };
    setSelection(span.current);
  };

  const onGroundMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drawing.current || span.current === null) return;
    span.current = { ...span.current, to: minuteAt(event.clientY) };
    setSelection(span.current);
  };

  // A press that never crossed a quarter hour is a click on empty ground, and a
  // click is not a claim about the day.
  const onGroundUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const drawnSpan = span.current;
    span.current = null;
    if (drawnSpan !== null && drawnSpan.from !== drawnSpan.to) {
      const drawn = draftGeometry(drawnSpan, startMinute);
      onDraw({
        startTs: etMinuteToTs(data.date, drawn.startMinute),
        durationMinutes: drawn.durationMinutes,
      });
    }
    setSelection(null);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Colour carries the source on the blocks themselves, which only works
          if the key is on the page: an unexplained colour is a private code,
          not a signal. The key doubles as the filter, so what switches a
          source off is the same mark that names it. */}
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-16 text-[11px] text-text-tertiary">
        {(Object.keys(CALENDAR_SOURCE_NAMES) as Array<keyof typeof CALENDAR_SOURCE_NAMES>).map(
          (kind) => {
            const shown = !hiddenSources.has(kind);
            return (
              <li key={kind}>
                <button
                  type="button"
                  aria-pressed={shown}
                  onClick={() => onToggleSource(kind)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-8 hover:text-text-secondary',
                    !shown && 'line-through opacity-50',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn('size-2 rounded-full', CALENDAR_SOURCE_STYLE[kind].accent)}
                  />
                  {CALENDAR_SOURCE_NAMES[kind]}
                </button>
              </li>
            );
          },
        )}
        <li className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-2.5 rounded-[2px] border border-dashed border-text-tertiary bg-background"
          />
          {BLOCK_STATE_LABELS.proposed}
        </li>
        <li className="flex items-center gap-1.5">
          <Check aria-hidden className="size-3 text-text-secondary" />
          {BLOCK_STATE_LABELS.logged}
        </li>
      </ul>

      {/* A long day scrolls rather than pushing the page down, and the axis
          travels inside the same scroller so an hour label never parts company
          with its own rule. */}
      <div
        ref={scrollerRef}
        data-testid="timeline-scroller"
        className="max-h-[70vh] overflow-y-auto"
      >
        <div className="flex gap-2 pr-1">
          <div className="w-14 shrink-0" style={{ height: `${gridHeight}px` }}>
            {hours.map((hour, index) => (
              <div
                key={hour}
                className="relative text-right text-[11px] tabular-nums text-text-tertiary"
                style={{ height: `${CALENDAR_HOUR_HEIGHT}px` }}
              >
                {/* Centred on its own rule rather than sitting above it, so the
                  label reads as naming the line and not the band below it. The
                  first rule is the container's own edge and needs no label. */}
                <span className="absolute right-0 top-0 -translate-y-1/2 pr-2">
                  {index === 0 ? '' : hourLabel(hour)}
                </span>
              </div>
            ))}
          </div>

          {/* The grid is the drawing surface, so the gesture is read here rather
              than on a layer of its own: a gap has to stay hoverable, and a
              transparent sheet over the day would swallow that. */}
          <div
            ref={groundRef}
            data-testid="timeline-ground"
            onPointerDown={onGroundDown}
            onPointerMove={onGroundMove}
            onPointerUp={onGroundUp}
            onPointerCancel={onGroundUp}
            className="relative flex-1 touch-none select-none border-l border-border-faint"
            style={{ height: `${gridHeight}px` }}
          >
            {hours.map((hour, index) => (
              <div
                key={hour}
                aria-hidden
                className="pointer-events-none absolute inset-x-0 border-t border-border-faint"
                style={{ top: `${index * CALENDAR_HOUR_HEIGHT}px` }}
              />
            ))}

            {/* Absence, not content: a gap is ground the day is missing, so it
              carries no border and says nothing until it is pointed at. It is
              named, never actionable — the way to fill it is to draw across it. */}
            {data.gaps.map((gap) => (
              <div
                key={`gap-${gap.startTs}`}
                className="group/gap absolute inset-x-0 pr-2"
                style={{
                  top: `${offsetOf(etMinutesOfDay(gap.startTs))}px`,
                  height: `${gap.minutes * CALENDAR_PX_PER_MINUTE}px`,
                }}
              >
                <div className="flex h-full items-center rounded-8 bg-muted/60 px-2 opacity-0 transition-opacity group-hover/gap:opacity-100">
                  <span className="truncate text-[11px] text-text-tertiary">
                    {minutesToHm(gap.minutes)} unaccounted
                  </span>
                </div>
              </div>
            ))}

            {data.rows.map((row) => {
              const { top, height, column, columns } = layout.get(row.id)!;
              return (
                <CalendarBlock
                  key={row.id}
                  row={row}
                  taskTypeName={
                    row.taskTypeName ??
                    ((row.taskTypeId && typeNameById.get(row.taskTypeId)) || null)
                  }
                  top={top}
                  height={height}
                  column={column}
                  columns={columns}
                  busy={busyRowId !== null && busyRowId !== row.id}
                  onConfirm={onConfirm}
                  onDismiss={onDismiss}
                  onDelete={onDelete}
                  onChangeDuration={(r, minutes) => settle(r, () => onChangeDuration(r, minutes))}
                  onToggleBillable={onToggleBillable}
                  onEdit={onEdit}
                  onMove={(r, startTs) => settle(r, () => onMove(r, startTs))}
                  onPreview={onPreview}
                  scrollerRef={scrollerRef}
                  readOnly={readOnly}
                />
              );
            })}

            {draft !== null && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 z-20 pr-2"
                style={{ top: `${draft.top}px`, height: `${draft.height}px` }}
              >
                <div className="flex h-full items-center rounded-8 border border-dashed border-primary bg-primary/10 px-2">
                  <span className="text-[11px] tabular-nums text-text-tertiary">
                    {minutesToHm(draft.durationMinutes)}
                  </span>
                </div>
              </div>
            )}

            {now !== null && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 border-t border-primary"
                style={{ top: `${offsetOf(now)}px` }}
              >
                <span className="absolute -left-1 -top-1 size-2 rounded-full bg-primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
